import React, { useEffect, useState } from 'react';
import { Users, CalendarDays, Check } from 'lucide-react';
import { foundations as f } from '../../data/programDetails';
import { nextMonthlyRelease, fmtDate } from '../../lib/winter';
import ProgramWaitlistForm from './ProgramWaitlistForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SeatBadge({ seats }) {
  if (!seats) return null;
  const full = seats.open_seats === 0;
  return (
    <div data-testid="foundations-seat-status" className={`inline-flex flex-wrap items-center gap-x-2 rounded-full px-4 py-2 text-sm font-semibold ${full ? 'bg-splash-coral/15 text-splash-coralDark' : 'bg-splash-teal/15 text-splash-tealDark'}`}>
      <Users className="w-4 h-4" />
      {full ? 'Currently full — join the waitlist' : `${seats.open_seats} of ${seats.capacity} seats open`}
      <span className="opacity-75">&middot; Next start: {seats.next_start_month}</span>
    </div>
  );
}

export default function FoundationsEnrollBlock() {
  const [seats, setSeats] = useState(null);
  const next = nextMonthlyRelease();
  useEffect(() => {
    fetch(`${API}/foundations/seats`).then((r) => r.json()).then(setSeats).catch(() => {});
  }, []);

  return (
    <section id="enroll" className="py-20 bg-splash-creamDark scroll-mt-24" data-testid="foundations-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Pricing &amp; Enrollment</div>
          <h2 className="font-display font-bold text-4xl md:text-5xl text-splash-green leading-tight">Simple, transparent pricing</h2>
          <div className="mt-8 rounded-3xl bg-white border border-splash-sand p-6 md:p-8" data-testid="foundations-price-card">
            <div className="text-xs uppercase tracking-widest text-splash-coral font-semibold">{f.name}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-display font-bold text-5xl text-splash-green">{f.perLesson}</span>
              <span className="text-splash-green/75">/ private lesson</span>
            </div>
            <p className="mt-1 text-sm text-splash-green/70" data-testid="foundations-price-line">{f.lessons} lessons &middot; {f.total} total, paid monthly</p>
            <dl className="mt-6 divide-y divide-splash-sand border-y border-splash-sand" data-testid="foundations-price-breakdown">
              {[['Month 1', '4 lessons', f.firstMonth], ['Month 2', '8 lessons', f.laterMonths], ['Month 3', '8 lessons', f.laterMonths]].map(([m, l, p]) => (
                <div key={m} className="flex items-baseline justify-between py-3">
                  <dt className="text-splash-green"><span className="font-semibold">{m}</span> <span className="text-sm text-splash-green/60">&middot; {l}</span></dt>
                  <dd className="font-semibold text-splash-green">{p}</dd>
                </div>
              ))}
            </dl>
            <ul className="mt-6 space-y-2 text-splash-green/85">
              {[`${f.lessons} private (1:1) lessons, ${f.minutes} minutes each`, '12-week program, starting the first week of the month', `Capped at ${f.seats} seats per start`, `${f.waitlistFee} waitlist fee holds your place when seats are full`].map((x) => (
                <li key={x} className="flex gap-2"><Check className="w-5 h-5 text-splash-teal shrink-0 mt-0.5" /> {x}</li>
              ))}
            </ul>
            <div className="mt-6"><SeatBadge seats={seats} /></div>
          </div>
          <p className="mt-6 flex gap-2 text-splash-green/85" data-testid="foundations-monthly-release">
            <CalendarDays className="w-5 h-5 text-splash-coral shrink-0 mt-0.5" />
            <span>{next ? <>Next enrollment opens <strong className="text-splash-coral">{fmtDate(next.release)}</strong> for {next.forMonth} starts.</> : 'Enrollment opens monthly.'}</span>
          </p>
        </div>
        <div className="rounded-3xl bg-white border border-splash-sand p-6 md:p-8">
          <div className="text-xs uppercase tracking-widest text-splash-coral font-semibold">Reserve a seat</div>
          <h3 className="mt-2 font-display font-bold text-2xl text-splash-green">Join the Little Splash Foundations™ waitlist</h3>
          <p className="mt-2 mb-6 text-sm text-splash-green/75">We&rsquo;ll reach out with your spot and the {f.waitlistFee} waitlist fee link before enrollment opens.</p>
          <ProgramWaitlistForm program="foundations" cta="Join the Little Splash Foundations™ waitlist" />
        </div>
      </div>
    </section>
  );
}
